// The words and rules for people's logins: Admin → a workspace → Accounts,
// Admin → People, the /set-password page and Settings → Password. Pure, no
// React; self-test in accountModel.selftest.ts (npm run check:format).
//
// The server decides everything (routes/admin_accounts.py,
// routes/auth_password.py); the rules here only say it in words and catch a
// password the server would refuse before the round trip.

import { formatDateTime, formatRelative, type DateInput } from "@/lib/format";

export type LoginStatus = "active" | "invited" | "disabled";
export type LoginRole = "customer" | "admin" | "family";
export type SignInVia = "web" | "app" | "passkey" | "link" | "google";
export type LinkPurpose = "invite" | "reset";

/** The server's own floor (users.PASSWORD_MIN_CHARS) and bcrypt's ceiling. */
export const PASSWORD_MIN_CHARS = 12;
export const PASSWORD_MAX_BYTES = 72;

export function statusLabel(status: string): string {
  if (status === "active") return "Active";
  if (status === "invited") return "Invited";
  if (status === "disabled") return "Disabled";
  return status;
}

export function statusTone(status: string): "success" | "info" | "warning" | "default" {
  if (status === "active") return "success";
  if (status === "invited") return "info";
  if (status === "disabled") return "warning";
  return "default";
}

export function roleLabel(role: string): string {
  if (role === "customer") return "Operator";
  if (role === "admin") return "Admin";
  if (role === "family") return "Child";
  return role;
}

const VIA: Record<SignInVia, string> = { web: "web", app: "app", passkey: "passkey", link: "link", google: "Google" };

/** "2 h ago · app", or null when it has never signed in. */
export function lastSignIn(at: string | null | undefined, via: string | null | undefined, now?: DateInput): string | null {
  if (!at) return null;
  const when = formatRelative(at, now, { inline: true });
  const how = via && via in VIA ? VIA[via as SignInVia] : null;
  return how ? `${when} · ${how}` : when;
}

/** The one meta line under a login: last sign-in and failures since. */
export function signInLine(
  a: { last_sign_in_at?: string | null; last_sign_in_via?: string | null; failed_since?: number | null; status?: string },
  now?: DateInput,
): string {
  const last = lastSignIn(a.last_sign_in_at, a.last_sign_in_via, now);
  const parts = [last ? `Last sign-in ${last}` : "Never signed in"];
  const failed = a.failed_since ?? 0;
  if (failed > 0) parts.push(failed === 1 ? "1 failed try" : `${failed} failed tries`);
  return parts.join(" · ");
}

/** "Expires Wed 28 Oct, 14:00" / "Expires Tomorrow 09:30". */
export function expiresText(at: string | null | undefined, now?: DateInput): string {
  if (!at) return "";
  return `Expires ${formatDateTime(at, { inline: true }, now)}`;
}

/** "Link expires tomorrow 09:30": an unused sign-in link waiting on a login. */
export function pendingLinkText(at: string | null | undefined, now?: DateInput): string | null {
  if (!at) return null;
  return `Link expires ${formatDateTime(at, { inline: true }, now)}`;
}

export function linkTitle(purpose: string): string {
  return purpose === "invite" ? "Invite link" : "Sign-in link";
}

export function addLoginError(code: string | null | undefined): string {
  switch (code) {
    case "already_exists":
      return "That username is taken.";
    case "reserved":
      return "That username belongs to another workspace.";
    case "invalid_username":
      return "Use lowercase letters, digits and . _ @ -";
    case "invalid_email":
      return "That email does not look right.";
    case "invalid_name":
      return "Use a shorter name.";
    default:
      return "That did not work. Try again.";
  }
}

const USERNAME_RE = /^[a-z0-9][a-z0-9._@-]{1,63}$/;

/** The username as the server will keep it (trimmed, lower case). */
export function normaliseUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Why a username would be refused, before asking the server. */
export function usernameProblem(raw: string): string | null {
  const name = normaliseUsername(raw);
  if (!name) return "Enter a username.";
  if (name.length < 2) return "Use at least 2 characters.";
  if (!USERNAME_RE.test(name)) return addLoginError("invalid_username");
  return null;
}

// ─── passwords people choose ───

function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** The server's password_problem, in its own codes. */
export function passwordCode(username: string, password: string): "too_short" | "too_long" | "same_as_username" | null {
  if ([...password].length < PASSWORD_MIN_CHARS) return "too_short";
  if (utf8Bytes(password) > PASSWORD_MAX_BYTES) return "too_long";
  if (password.trim().toLowerCase() === normaliseUsername(username)) return "same_as_username";
  return null;
}

export function passwordProblemText(code: string | null | undefined): string {
  switch (code) {
    case "too_short":
      return `Use at least ${PASSWORD_MIN_CHARS} characters.`;
    case "too_long":
      return "Use a shorter password.";
    case "same_as_username":
      return "Use something other than your username.";
    default:
      return "Choose another password.";
  }
}

export type LinkState = "link_unknown" | "link_used" | "link_void" | "link_expired" | "login_gone";

export function linkErrorText(code: string | null | undefined): string {
  switch (code) {
    case "link_used":
      return "This link has been used.";
    case "link_expired":
      return "This link has expired.";
    case "link_void":
      return "This link no longer works.";
    case "login_gone":
      return "This login is no longer available.";
    default:
      return "This link does not work.";
  }
}

export function setPasswordTitle(purpose: string | null | undefined): string {
  return purpose === "reset" ? "New password" : "Set your password";
}

/** The token in a /set-password#lat_pl_… URL's fragment, or null. */
export function tokenFromHash(hash: string): string | null {
  const raw = hash.replace(/^#/, "").trim();
  return /^lat_pl_[A-Za-z0-9_-]{8,}$/.test(raw) ? raw : null;
}

// ─── Admin → People ───

export interface PersonRow {
  username: string;
  customer_id?: string | null;
  role: string;
  status: string;
  name?: string | null;
  email?: string | null;
  last_sign_in_at?: string | null;
}

/** Rows matching every word of the query (name, username, workspace, email, role). */
export function filterPeople<T extends PersonRow>(rows: T[], query: string): T[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rows;
  return rows.filter((r) => {
    const hay = [r.username, r.name, r.email, r.customer_id, roleLabel(r.role), statusLabel(r.status)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Where a person's row opens: their workspace's Accounts tab (children and
 *  admins have none to manage there). */
export function personLink(r: PersonRow): string | null {
  if (r.role !== "customer" || !r.customer_id) return null;
  return `/admin/customer/${encodeURIComponent(r.customer_id)}?tab=people`;
}

export function peopleCounts(rows: PersonRow[]): { total: number; invited: number; disabled: number } {
  return {
    total: rows.length,
    invited: rows.filter((r) => r.status === "invited").length,
    disabled: rows.filter((r) => r.status === "disabled").length,
  };
}
